import { z } from 'zod';
import { finiteOr } from '../utils/numbers';

const decimals = z.number().int().min(0).max(20).optional();
const chatTableFormatSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('text') }),
  z.object({ kind: z.literal('number'), decimals, compact: z.boolean().optional(), unit: z.string().optional() }),
  z.object({ kind: z.literal('currency'), currency: z.string(), decimals, compact: z.boolean().optional() }),
  z.object({ kind: z.literal('date'), style: z.enum(['date', 'datetime', 'relative']).optional() }),
  z.object({ kind: z.literal('link'), hrefKey: z.string().optional() }),
  z.object({
    kind: z.literal('badge'),
    tones: z.record(z.string(), z.enum(['neutral', 'success', 'warning', 'danger'])).optional(),
  }),
]);

export const chatTableColumnSchema = z.object({
  key: z.string(),
  label: z.string(),
  format: chatTableFormatSchema.optional(),
  align: z.enum(['start', 'end']).optional(),
  sortable: z.boolean().optional(),
  priority: z.enum(['primary', 'secondary']).optional(),
  width: z.string().optional(),
});

const chatTableValueSchema = z.union([
  z.string(),
  z.number().finite(),
  z.boolean(),
  z.null(),
  z.array(z.union([z.string(), z.number().finite()])),
]);

const table = { columns: z.array(chatTableColumnSchema), rows: z.array(z.record(z.string(), chatTableValueSchema)) };

/** Where a document's layout onboarding stands: the API's DocumentOnboardingStatus. */
export const CHAT_DOCUMENT_ONBOARDING_STATUSES = [
  'DRAFTING',
  'READY',
  'UNSUPPORTED',
  'DRAFT_FAILED',
  'MAPPING',
  'MAP_FAILED',
  'READING',
  'READ_FAILED',
  'DONE',
] as const;

/** A document held for its layout, as its Review card names it. The card reads where the layout stands now. */
const chatDocumentOnboardingSchema = z.object({
  documentId: z.number().int().positive(),
  /** Null when the onboarding could not be read as the user was told. */
  documentOnboardingId: z.number().int().positive().nullable(),
  title: z.string(),
  /** Where the layout stood when the user was told. */
  status: z.enum(CHAT_DOCUMENT_ONBOARDING_STATUSES).nullable(),
});

export const chatPresentationSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('order'),
    ...table,
    order: z.object({
      type: z.enum(['SO', 'PO']),
      id: z.union([z.number().int(), z.string()]),
      slug: z.string().nullable(),
      party: z.string().nullable(),
      status: z.string().nullable(),
      shipDate: z.string().nullable(),
      cartons: z.number().finite().nullable(),
      total: z.number().finite().nullable(),
      currency: z.string().nullable(),
      href: z.string().nullable(),
      external: z
        .object({ system: z.string(), syncStatus: z.string(), syncedAt: z.string().nullable().optional() })
        .optional(),
    }),
  }),
  z.object({ kind: z.literal('order-list'), ...table }),
  z.object({ kind: z.literal('table'), title: z.string().optional(), description: z.string().optional(), ...table }),
  z.object({ kind: z.literal('document-onboarding'), documents: z.array(chatDocumentOnboardingSchema).min(1) }),
]);

export type ChatPresentation = z.infer<typeof chatPresentationSchema>;
export type ChatTableColumn = z.infer<typeof chatTableColumnSchema>;
export type ChatDocumentOnboarding = z.infer<typeof chatDocumentOnboardingSchema>;

/** The Review cards of documents held for their layout. */
export function createDocumentOnboardingPresentation(documents: readonly ChatDocumentOnboarding[]): ChatPresentation {
  return chatPresentationSchema.parse({ kind: 'document-onboarding', documents });
}

export type ChatOrder = Extract<ChatPresentation, { kind: 'order' }>['order'];
export interface ChatOrderLine {
  index: number;
  description: string;
  quantity: number | null;
  unitPrice: number | null;
}

export function createOrderPresentation(
  order: Pick<ChatOrder, 'type' | 'id'> & Partial<Omit<ChatOrder, 'type' | 'id'>>,
  items: ChatOrderLine[] = []
): ChatPresentation {
  return chatPresentationSchema.parse({
    kind: 'order',
    order: {
      ...order,
      slug: order.slug ?? null,
      party: order.party ?? null,
      status: order.status ?? null,
      shipDate: order.shipDate ?? null,
      cartons: order.cartons ?? null,
      total: order.total ?? null,
      currency: order.currency ?? null,
      href: order.href ?? null,
    },
    columns: [
      { key: 'index', label: '#', format: { kind: 'number' } },
      { key: 'description', label: 'Product', priority: 'primary' },
      { key: 'quantity', label: 'Quantity', format: { kind: 'number', unit: 'cartons' }, align: 'end' },
      {
        key: 'unitPrice',
        label: 'Unit price',
        format: order.currency ? { kind: 'currency', currency: order.currency } : { kind: 'number', decimals: 2 },
        align: 'end',
      },
    ],
    rows: items,
  });
}

export function createOrderListPresentation(
  orders: Array<{
    reference: string;
    href: string;
    party?: string | null;
    status?: string | null;
    shipDate?: string | null;
  }>
): ChatPresentation {
  return chatPresentationSchema.parse({
    kind: 'order-list',
    columns: [
      { key: 'reference', label: 'Order', format: { kind: 'link', hrefKey: 'href' }, priority: 'primary' },
      { key: 'party', label: 'Customer / vendor' },
      { key: 'status', label: 'Status', format: { kind: 'badge' } },
      { key: 'shipDate', label: 'Ship date', format: { kind: 'date' } },
    ],
    rows: orders.map((order) => ({
      reference: order.reference,
      href: order.href,
      party: order.party ?? null,
      status: order.status ?? null,
      shipDate: order.shipDate ?? null,
    })),
  });
}

interface ChatOrderState {
  id?: number;
  slug?: string;
  customer?: { name?: string };
  shipDate?: string;
  currencyCode?: string;
  items: Array<{ description: string; productDescription?: string; quantity?: string | null; price?: string | null }>;
}

const finiteNumber = (value: string | null | undefined): number | null => {
  if (value == null || value.trim() === '') return null;
  return finiteOr(Number(value), null);
};

export function createOrderStatePresentation(
  type: ChatOrder['type'],
  state: ChatOrderState
): ChatPresentation | undefined {
  if (state.id === undefined) return undefined;
  const quantities = state.items.map((item) => finiteNumber(item.quantity));
  return createOrderPresentation(
    {
      type,
      id: state.id,
      slug: state.slug ?? null,
      party: state.customer?.name ?? null,
      shipDate: state.shipDate ?? null,
      currency: state.currencyCode ?? null,
      cartons: quantities.some((quantity) => quantity === null)
        ? null
        : quantities.reduce<number>((sum, quantity) => sum + (quantity ?? 0), 0),
      href: state.slug
        ? `/${type === 'SO' ? 'sales-orders' : 'purchase-orders'}/${encodeURIComponent(state.slug)}`
        : null,
    },
    state.items.map((item, index) => ({
      index: index + 1,
      description: item.productDescription || item.description,
      quantity: finiteNumber(item.quantity),
      unitPrice: finiteNumber(item.price),
    }))
  );
}

export function createErpOrderStatePresentation(
  type: ChatOrder['type'],
  state: ChatOrderState,
  external: { system: string; id?: number }
): ChatPresentation | undefined {
  const id = external.id ?? state.id;
  const presentation = createOrderStatePresentation(type, { ...state, id });
  if (presentation?.kind !== 'order') return undefined;
  return chatPresentationSchema.parse({
    ...presentation,
    order: {
      ...presentation.order,
      id,
      href: null,
      external: { system: external.system, syncStatus: external.id == null ? 'Unknown' : 'Saved' },
    },
  });
}

export function createErpOrderListPresentation(
  type: ChatOrder['type'],
  orders: Array<{ orderNumber: string; customer?: string; vendor?: string; status?: string }>,
  system: string
): ChatPresentation {
  return chatPresentationSchema.parse({
    kind: 'order-list',
    columns: [
      { key: 'reference', label: 'Order', priority: 'primary' },
      { key: 'party', label: 'Customer / vendor' },
      { key: 'status', label: 'Status', format: { kind: 'badge' } },
      { key: 'system', label: 'ERP', format: { kind: 'badge' } },
    ],
    rows: orders.map((order) => ({
      reference: `${system} ${type} #${order.orderNumber}`,
      party: order.customer ?? order.vendor ?? null,
      status: order.status ?? null,
      system,
    })),
  });
}
